import { NotificationType, Screen, useUserInfo, useUserSettings } from "@next-platform/client-common-core";
import { useCallback } from "react";

import { useRecombee, useToast } from "@/hooks";

/** Shared across card and overlay instances so two hearts cannot toggle the same chatbot at once. */
const inFlightFavorites = new Map<string, Promise<unknown>>();

type UseDiscoverFavoriteOptions = {
  /** Runs instead of the request while the viewer is a guest. */
  readonly onGuest: () => void;
};

/** Favorite toggle shared by Discovery cards and the profile overlay; guests are handed to `onGuest` instead. */
export default function useDiscoverFavorite({ onGuest }: UseDiscoverFavoriteOptions) {
  const { isAuthenticated, isAuthLoaded, onlyUserId } = useUserInfo();
  const { likedBots, onUserLikebot } = useUserSettings();
  const { sendUserInteractionAddBookmark } = useRecombee();
  const { displayToast } = useToast();

  return useCallback(
    (characterId: string) => {
      // Until the session restores a signed-in viewer still reads as a guest, so the click is dropped rather than misrouted.
      if (!isAuthLoaded) {
        return;
      }

      if (!isAuthenticated) {
        onGuest();
        return;
      }

      const inFlightFavorite = inFlightFavorites.get(characterId);
      if (inFlightFavorite) {
        return inFlightFavorite;
      }

      const isCurrentlyFavorite = Boolean(likedBots?.includes(characterId));
      if (!isCurrentlyFavorite && onlyUserId) {
        void sendUserInteractionAddBookmark(onlyUserId, characterId);
      }

      // The caller waits on this so it can flip the heart before the store updates.
      const favoriteRequest = onUserLikebot(characterId, Screen.DISCOVER)
        .catch(() => displayToast({ type: NotificationType.error }))
        .finally(() => {
          if (inFlightFavorites.get(characterId) === favoriteRequest) {
            inFlightFavorites.delete(characterId);
          }
        });
      inFlightFavorites.set(characterId, favoriteRequest);
      return favoriteRequest;
    },
    [displayToast, isAuthLoaded, isAuthenticated, likedBots, onGuest, onUserLikebot, onlyUserId, sendUserInteractionAddBookmark],
  );
}
